// src/components/hub/work/filters/WorkBucketTabs.js
import React, { useRef, useEffect } from "react";
import ThinScroll from "../../../shared/ThinScroll";
import { fmtCount } from "../../../shared/format";
import { BUCKETS } from "../constants";

// Bucket tab row + "New task" button. The outer div keeps id="work-bucket-tabs"
// — the verdict-focus and takeaway-navigation handlers scrollIntoView it, so the
// id must stay on the page's rendered output.
const WorkBucketTabs = ({
  counts,
  activeCache,
  includeDone,
  activeBucket,
  disabled,
  onSelect,
  onCreate,
  recurringCount,
}) => {
  // Active-tab ref — when activeBucket changes (incl. the "recurring" tab),
  // scroll the active tab into view so it stays visible inside the mobile
  // horizontally-scrollable tab row (work-page-fixes §C, Item 2). block/inline
  // "nearest" nudges it horizontally without forcing a vertical page jump.
  const activeTabRef = useRef(null);
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [activeBucket]);

  return (
  <div id="work-bucket-tabs" className="border-b border-gray-200 dark:border-gray-800 scroll-mt-24">
    <div className="flex items-center gap-3">
      <ThinScroll className="flex-1 flex gap-1 overflow-x-auto whitespace-nowrap relative pb-1.5 md:pb-0 -mb-px" role="tablist">
        {BUCKETS.map((b) => {
          const active = activeBucket === b.key;
          // `counts` holds each bucket's own count (own-only — done is never
          // baked into entry.counts; see useWorkBucketCache §B). The active tab
          // adds the filtered done count (stored separately on
          // activeCache.doneCount) only when includeDone is on, so done is
          // displayed once for the active bucket and never accumulates across
          // bucket switches. Non-active tabs show their own count only. The
          // count always comes from the authoritative `counts` object (seeded
          // from data.counts), never from items.length — the items array may
          // contain done supplement rows while counts reflects the backend totals.
          const ownCount = counts[b.key] ?? null;
          const count = ownCount != null
            ? ownCount + (active && includeDone ? (activeCache.doneCount ?? 0) : 0)
            : null;
          return (
            <button
              key={b.key}
              type="button"
              role="tab"
              ref={active ? activeTabRef : null}
              aria-selected={active}
              onClick={() => onSelect(b.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap flex-shrink-0 transition-colors ${
                active
                  ? "border-[#32B1FF] text-[#32B1FF]"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              }`}
            >
              {b.label}
              {count != null && (
                <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">{fmtCount(count)}</span>
              )}
            </button>
          );
        })}
        {/* Recurring tab (work-page-fixes Item 1) — same styling as the bucket
            tabs. Selecting it sets activeBucket to "recurring", which triggers
            the recurringList() fetch + id-set filter in WorkPage.js. */}
        <button
          key="recurring"
          type="button"
          role="tab"
          ref={activeBucket === "recurring" ? activeTabRef : null}
          aria-selected={activeBucket === "recurring"}
          onClick={() => onSelect("recurring")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap flex-shrink-0 transition-colors ${
            activeBucket === "recurring"
              ? "border-[#32B1FF] text-[#32B1FF]"
              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          }`}
        >
          Recurring
          {recurringCount != null && (
            <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">{fmtCount(recurringCount)}</span>
          )}
        </button>
        {/* Mobile scroll affordance (work-page-fixes §C, Item 3): a right-edge
            gradient fade hinting more tabs are off-screen. The overlay is an
            absolutely-positioned child of the relative scroll container, so it
            stays pinned to the right edge and does not scroll with the tabs.
            `md:hidden` keeps it on mobile only — desktop shows every tab with
            no scrolling, so no fade is needed there. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-0 right-0 z-10 h-full w-10 bg-gradient-to-l from-gray-50 dark:from-gray-950 to-transparent md:hidden"
        />
      </ThinScroll>
      {/* "+ New task" button — always visible. A new repeating task is created
          via the New task modal's repeat section (work-page-fixes §C). */}
      <button type="button" onClick={onCreate} disabled={disabled}
        title="Create new task"
        className="shrink-0 mr-1.5 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-700 bg-[#32B1FF]/5 text-[#32B1FF] cursor-pointer hover:bg-[#32B1FF]/10 dark:bg-[#32B1FF]/10 dark:text-[#32B1FF] dark:hover:bg-[#32B1FF]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
        New task
      </button>
    </div>
  </div>
  );
};

export default WorkBucketTabs;
